import { defineComponent } from "vue";

import { COMPONENT_NAME, SVG } from "./attributes";

import { UIButton } from "@/components/ui";
import { SharedWindow } from "@/components/shared";
import {
  WalletListResponse,
  WalletWithAmountResponse,
} from "@/shared/repository/modules/wallet/repo";
import { ApplicationStoreRequest } from "@/shared/repository/modules/application/repo";
import { SvgAttribute } from "@/shared/constants/constants";
import {
  formatFiatCurrency,
  formatCryptoCurrency,
} from "@/shared/utils/amount-helpers";
import { AdminCurrencyType } from "@/shared/repository/modules/admin/currency/enums";

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    UIButton,
    SharedWindow,
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    isResult: {
      type: Boolean,
      default: false,
    },
    isConfirm: {
      type: Boolean,
      default: false,
    },
    isConfirmWalletExchange: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    "update:isLoading": null,
    "update:isResult": null,
    "update:isConfirm": null,
    "update:isConfirmWalletExchange": null,
  },
  data() {
    return {
      userRepo: this.$projectServices.userRepo,
      walletRepo: this.$projectServices.walletRepo,
      walletRepository: this.$projectServices.walletRepository,
      applicationRepo: this.$projectServices.applicationRepo,
      applicationRepository: this.$projectServices.applicationRepository,
    };
  },
  computed: {
    displayedWalletList(): WalletListResponse[] {
      return this.walletRepo.list;
    },

    displayedWalletWithAmount(): WalletWithAmountResponse[] {
      return this.walletRepo.withAmount;
    },

    displayedApplicationStoreRequest(): ApplicationStoreRequest {
      return this.applicationRepo.application;
    },

    displayedSvg(): SvgAttribute {
      return SVG;
    },

    displayedCurrencyFrom(): WalletListResponse {
      const { currency_id_from } = this.displayedApplicationStoreRequest;
      return this.displayedWalletList.find(
        (currencyItem) => currencyItem.currency_id === currency_id_from
      )!;
    },

    displayedCurrencyTo(): WalletListResponse {
      const { currency_id_to } = this.displayedApplicationStoreRequest;

      return this.displayedWalletList.find(
        (currencyItem) => currencyItem.currency_id === currency_id_to
      )!;
    },

    displayedCommissions(): string {
      return `~ ${this.displayedCommission} ${
        this.displayedCurrencyFrom.name.toUpperCase() || ""
      }`;
    },

    displayedCourse(): string {
      if (
        this.displayedApplicationStoreRequest.currency_type_to ===
        AdminCurrencyType.crypto
      ) {
        return formatCryptoCurrency(
          this.displayedApplicationStoreRequest.execution_price
        );
      } else {
        return formatFiatCurrency(
          this.displayedApplicationStoreRequest.execution_price
        );
      }
    },

    displayedCommission(): string {
      const { currency_type_from } = this.displayedApplicationStoreRequest;
      if (currency_type_from === AdminCurrencyType.crypto) {
        return formatCryptoCurrency(
          this.displayedApplicationStoreRequest.commissions
        );
      } else {
        return formatFiatCurrency(
          this.displayedApplicationStoreRequest.commissions
        );
      }
    },

    displayedAmountFrom(): string {
      const { currency_type_from } = this.displayedApplicationStoreRequest;

      if (currency_type_from === AdminCurrencyType.crypto) {
        return formatCryptoCurrency(
          this.displayedApplicationStoreRequest.amount
        );
      } else if (currency_type_from === AdminCurrencyType.fiat) {
        return formatFiatCurrency(this.displayedApplicationStoreRequest.amount);
      }

      return "";
    },

    displayedAmountTo(): string {
      const { amount, commissions, execution_price, currency_type_to } =
        this.displayedApplicationStoreRequest;
      const currency = (amount - commissions) * execution_price;

      if (currency_type_to === AdminCurrencyType.crypto) {
        return formatCryptoCurrency(currency);
      } else if (currency_type_to === AdminCurrencyType.fiat) {
        return formatFiatCurrency(currency);
      }

      return "";
    },
  },
  methods: {
    handleCopy(): void {
      console.log("Handle Copy");
    },

    handleClose(): void {
      if (!this.isLoading) {
        this.$emit("update:isResult", false);
      }
    },

    async handleCreate(): Promise<void> {
      this.$emit("update:isLoading", true);
      try {
        const req = this.displayedApplicationStoreRequest;
        const userId = this.userRepo.userInfo.id;

        const basePayload = {
          currency_id_from: req.currency_id_from,
          currency_id_to: req.currency_id_to,
          currency_type_from: req.currency_type_from,
          currency_type_to: req.currency_type_to,
          amount: req.amount,
          commissions: req.commissions,
          execution_price: req.execution_price,
          transaction_volume: req.transaction_volume,
          max_amount: req.max_amount,
          pair: req.pair,
        };

        // fiat → fiat
        if (
          req.currency_type_from === AdminCurrencyType.fiat &&
          req.currency_type_to === AdminCurrencyType.fiat
        ) {
          await this.applicationRepository.store(basePayload, userId);
        }
        // crypto → fiat
        else if (
          req.currency_type_from === AdminCurrencyType.crypto &&
          req.currency_type_to === AdminCurrencyType.fiat
        ) {
          await this.applicationRepository.store(
            {
              ...basePayload,
              user_requisite_id_from: req.user_requisite_id_from,
              user_requisite_type_from: req.user_requisite_type_from,
            },
            userId
          );
        }
        // fiat → crypto
        else if (
          req.currency_type_from === AdminCurrencyType.fiat &&
          req.currency_type_to === AdminCurrencyType.crypto
        ) {
          await this.applicationRepository.store(
            {
              ...basePayload,
              user_requisite_id_to: req.user_requisite_id_to,
              user_requisite_type_to: req.user_requisite_type_to,
            },
            userId
          );
        }
        // crypto → crypto
        else if (
          req.currency_type_from === AdminCurrencyType.crypto &&
          req.currency_type_to === AdminCurrencyType.crypto
        ) {
          await this.walletRepository.exchange({
            from: req.currency_id_from,
            to: req.currency_id_to,
            amount: req.amount,
            created: new Date().toISOString(),
            userId,
            symbol: req.pair?.symbol || "",
            side: req.pair?.side || "",
          });
        }

        this.$emit("update:isResult", false);
        if (
          req.currency_type_from === AdminCurrencyType.crypto &&
          req.currency_type_to === AdminCurrencyType.crypto
        ) {
          this.$emit("update:isConfirmWalletExchange", true);
        } else {
          this.$emit("update:isConfirm", true);
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.$emit("update:isLoading", false);
      }
    },
  },
});
